// @ts-nocheck

import React, { useRef, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { supabase } from '@/integrations/supabase/client';
import { Image } from 'lucide-react';
import IconPicker from '@/components/email/IconPicker';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  value,
  onChange,
  placeholder,
  className = ''
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  
  // Image upload handler
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
          const filePath = `email-templates/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('notification-content')
            .upload(filePath, file, { upsert: true });
            
          if (uploadError) {
            throw uploadError;
          }
          
          const { data: { publicUrl } } = supabase.storage
            .from('notification-content')
            .getPublicUrl(filePath);
          
          // Insert image at cursor position
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            if (range) {
              quill.insertEmbed(range.index, 'image', publicUrl);
              quill.setSelection(range.index + 1, 0);
            }
          }
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      }
    };
  };

  const handleIconSelect = (iconSvg: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      if (range) {
        // Insert the icon SVG as HTML
        quill.clipboard.dangerouslyPasteHTML(range.index, iconSvg);
        quill.setSelection(range.index + 1, 0);
      }
    }
  };
  
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'image'
  ];

  return (
    <div className={`wysiwyg-editor ${className}`}>
      <div className="mb-2">
        <IconPicker onIconSelect={handleIconSelect} />
      </div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        style={{
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '6px'
        }}
      />
      <style>{`
        .wysiwyg-editor .ql-editor {
          min-height: 250px;
        }
        .wysiwyg-editor .ql-toolbar {
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background-color: #f8f9fa;
        }
        .wysiwyg-editor .ql-container {
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }
        .wysiwyg-editor .ql-toolbar button:hover {
          background-color: #e9ecef;
        }
        .wysiwyg-editor .ql-tooltip {
          z-index: 100;
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;